import React, { useEffect, useRef } from 'react';
import './UserContextMenu.css';

function UserContextMenu({ 
  user, 
  currentUser,
  position, 
  onClose, 
  onProfile,
  onMention,
  onMessage,
  onInviteToServer,
  onAddFriend,
  onBlock,
  servers = [],
  friends = []
}) {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  // Don't show context menu for yourself
  if (user.id === currentUser.id) {
    return null;
  }

  // Adjust position to keep menu on screen
  const adjustedPosition = { ...position };
  if (menuRef.current) {
    const rect = menuRef.current.getBoundingClientRect();
    if (rect.right > window.innerWidth) {
      adjustedPosition.x = window.innerWidth - rect.width - 10;
    }
    if (rect.bottom > window.innerHeight) {
      adjustedPosition.y = window.innerHeight - rect.height - 10;
    }
  }

  return (
    <div 
      className="user-context-menu" 
      ref={menuRef}
      style={{ 
        top: `${adjustedPosition.y}px`, 
        left: `${adjustedPosition.x}px` 
      }}
    >
      <div className="context-menu-header">
        <div className="context-menu-avatar">
          {user.username?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="context-menu-username">{user.username}</div>
      </div>

      <div className="context-menu-divider"></div>

      <div className="context-menu-item" onClick={onProfile}>
        <span className="context-menu-icon">👤</span>
        Profile
      </div>

      {onMention && (
        <div className="context-menu-item" onClick={onMention}>
          <span className="context-menu-icon">@</span>
          Mention
        </div>
      )}

      <div className="context-menu-item" onClick={onMessage}>
        <span className="context-menu-icon">💬</span>
        Message
      </div>

      {servers && servers.length > 0 && (
        <div className="context-menu-item context-menu-submenu">
          <span className="context-menu-icon">📨</span>
          Invite to Server
          <span className="context-menu-arrow">›</span>
          <div className="context-submenu">
            {servers.map(server => (
              <div 
                key={server.id}
                className="context-menu-item"
                onClick={() => onInviteToServer(server.id)}
              >
                {server.name}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="context-menu-divider"></div>

      {!friends.some(f => f.id === user.id) && (
        <div className="context-menu-item" onClick={onAddFriend}>
          <span className="context-menu-icon">➕</span>
          Add Friend
        </div>
      )}

      {user.roles && user.roles.length > 0 && (
        <>
          <div className="context-menu-divider"></div>
          <div className="context-menu-item context-menu-submenu">
            <span className="context-menu-icon">🎭</span>
            Roles
            <span className="context-menu-arrow">›</span>
            <div className="context-submenu">
              {user.roles.map((role, idx) => (
                <div 
                  key={idx}
                  className="context-menu-item"
                  style={{ color: role.color || '#ffffff' }}
                >
                  {role.name}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="context-menu-divider"></div>

      <div className="context-menu-item context-menu-danger" onClick={onBlock}>
        <span className="context-menu-icon">🚫</span>
        Block
      </div>
    </div>
  );
}

export default UserContextMenu;
